import React from 'react';
import { BarChart3, TrendingUp, Building2, FileText, Check } from 'lucide-react';
import type { ModuleType } from '../../types/demo';

// Map icon names to components
const iconMap = {
  'BarChart3': BarChart3,
  'TrendingUp': TrendingUp,
  'Building2': Building2,
  'FileText': FileText
};

interface ModuleCardProps {
  id: ModuleType;
  title: string;
  description: string;
  icon: keyof typeof iconMap;
  selected: boolean;
  onClick: () => void;
}

export function ModuleCard({
  id,
  title,
  description,
  icon,
  selected,
  onClick
}: ModuleCardProps) {
  const IconComponent = iconMap[icon];

  return (
    <button
      onClick={onClick}
      className={`
        relative w-full text-left p-6 rounded-xl border-2 transition-all duration-200
        ${selected
          ? 'border-gsp-teal bg-gsp-teal/5 shadow-lg'
          : 'border-gray-200 bg-white hover:border-gsp-teal/50 hover:shadow-md'
        }
      `}
    >
      {/* Selected indicator */}
      {selected && (
        <div className="absolute top-4 right-4 w-6 h-6 rounded-full bg-gsp-teal flex items-center justify-center">
          <Check className="w-4 h-4 text-white" />
        </div>
      )}

      {/* Icon */}
      <div className={`
        w-12 h-12 rounded-lg flex items-center justify-center mb-4
        ${selected ? 'bg-gsp-teal/20 text-gsp-teal' : 'bg-gray-100 text-gsp-navy'}
      `}>
        <IconComponent className="w-6 h-6" />
      </div>

      {/* Content */}
      <h3 className={`
        text-lg font-semibold mb-2
        ${selected ? 'text-gsp-teal' : 'text-gsp-text-primary'}
      `}>
        {title}
      </h3>
      <p className="text-sm text-gsp-text-body leading-relaxed">
        {description}
      </p>

      {/* CTA hint */}
      <div className={`
        mt-4 text-sm font-medium
        ${selected ? 'text-gsp-teal' : 'text-gsp-navy'}
      `}>
        {selected ? 'Selected' : 'Click to select'}
      </div>
    </button>
  );
}

export default ModuleCard;
